import React, { useMemo, useState } from "react";
import { api } from "../lib/api";
import { X, Warning } from "@phosphor-icons/react";

/**
 * Log a breakdown against a specific conveyor.
 * - Pick conveyor from the conveyors currently in any active route (or fallback to all)
 * - Pick accountability: Internal vs External (Transnet)
 * - Optional end time (leave blank for an ongoing breakdown)
 */
export default function ConveyorBreakdownModal({ vessel, conveyors, activeRoutes, onClose, onSubmitted }) {
    // Conveyors visible in any currently active route, deduped
    const activeConveyorNames = useMemo(() => {
        const s = new Set();
        (activeRoutes || []).forEach((r) => (r.conveyors || []).forEach((c) => s.add(c)));
        return Array.from(s);
    }, [activeRoutes]);

    const preferred = activeConveyorNames.length > 0
        ? (conveyors || []).filter((c) => activeConveyorNames.includes(c.name))
        : (conveyors || []);

    const [conveyorId, setConveyorId] = useState(preferred[0]?.id || "");
    const [reason, setReason] = useState("");
    const [accountability, setAccountability] = useState("internal");
    const [startTime, setStartTime] = useState(() => new Date().toISOString().slice(0, 16));
    const [endTime, setEndTime] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!conveyorId) {
            setErr("Please select a conveyor");
            return;
        }
        if (!reason.trim()) {
            setErr("Please describe the breakdown");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/conveyor-breakdowns", {
                conveyor_id: conveyorId,
                vessel_id: vessel?.id || null,
                start_time: startTime ? new Date(startTime).toISOString() : null,
                end_time: endTime ? new Date(endTime).toISOString() : null,
                reason: reason.trim(),
                accountability,
                notes: notes.trim(),
            });
            onSubmitted && onSubmitted();
            onClose();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Failed to log breakdown");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="conveyor-breakdown-modal">
            <form
                onSubmit={submit}
                className="bg-white rounded-sm shadow-xl w-full max-w-lg overflow-hidden border-t-4 border-[var(--grindrod-red)]"
            >
                <div className="px-5 py-4 flex items-center gap-2 border-b border-slate-100 bg-[var(--grindrod-red)] text-white">
                    <Warning size={18} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Log Conveyor Breakdown</div>
                    <button type="button" onClick={onClose} className="ml-auto opacity-80 hover:opacity-100" data-testid="close-conveyor-breakdown-btn">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500 font-semibold">Conveyor</label>
                        <select
                            value={conveyorId}
                            onChange={(e) => setConveyorId(e.target.value)}
                            className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-2 text-sm"
                            data-testid="breakdown-conveyor-select"
                        >
                            <option value="">— Select conveyor —</option>
                            {(preferred.length ? preferred : conveyors || []).map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name} {activeConveyorNames.includes(c.name) ? "(in active route)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500 font-semibold">Accountability</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            <button
                                type="button"
                                onClick={() => setAccountability("internal")}
                                className={`text-xs uppercase tracking-wide font-bold py-2 rounded-sm border ${accountability === "internal" ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}
                                data-testid="accountability-internal"
                            >
                                Internal
                            </button>
                            <button
                                type="button"
                                onClick={() => setAccountability("external")}
                                className={`text-xs uppercase tracking-wide font-bold py-2 rounded-sm border ${accountability === "external" ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-slate-600 border-slate-300"}`}
                                data-testid="accountability-external"
                            >
                                External · Transnet
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="uppercase-label text-[10px] text-slate-500 font-semibold">Start</label>
                            <input
                                type="datetime-local"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                                data-testid="breakdown-start-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label text-[10px] text-slate-500 font-semibold">End (optional)</label>
                            <input
                                type="datetime-local"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                                data-testid="breakdown-end-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500 font-semibold">Reason</label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Belt tear, motor trip, gearbox failure"
                            className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                            data-testid="breakdown-reason-input"
                        />
                    </div>

                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500 font-semibold">Notes (optional)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Actions taken, escalation, etc."
                            rows={2}
                            className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                            data-testid="breakdown-notes-input"
                        />
                    </div>

                    {err && (
                        <div className="text-xs text-[var(--grindrod-red)] bg-[#FDE7E9] border border-[var(--grindrod-red)] px-2 py-1.5 rounded-sm" data-testid="breakdown-error">
                            {err}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-outline text-xs" data-testid="cancel-conveyor-breakdown-btn">Cancel</button>
                    <button type="submit" disabled={submitting} className="btn-primary text-xs disabled:opacity-40" data-testid="submit-conveyor-breakdown-btn">
                        {submitting ? "Logging…" : "Log breakdown"}
                    </button>
                </div>
            </form>
        </div>
    );
}
